import React, { useState, useMemo, useEffect } from 'react';
import {
    Eye,
    Download,
    BarChart3,
    ChevronLeft,
    ChevronRight,
    X,
    Trash2,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { formatCurrency, formatDate, subscriptionAPI, bondsAPI, filesAPI, subAgreementRequestsAPI, subAgreementTemplatesAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const SubmitSubAgreement = () => {
    const { getDisplayName, user } = useAuth();
    const queryClient = useQueryClient();
    const [selectedBond, setSelectedBond] = useState(null);
    const [showDetails, setShowDetails] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [showSubscribeModal, setShowSubscribeModal] = useState(false);
    const [subscriptionBondId, setSubscriptionBondId] = useState('');
    const [subscriptionAmount, setSubscriptionAmount] = useState('');
    const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showRequestDetails, setShowRequestDetails] = useState(false);
    const [submissionDownloadId, setSubmissionDownloadId] = useState(null);
    const [selectedBondData, setSelectedBondData] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'submission' | 'request', requestId, submissionId? }
    const [isDeletingSubmission, setIsDeletingSubmission] = useState(false);
    const [isDeletingRequest, setIsDeletingRequest] = useState(false);

    const { data: availableBondsResp, isPending: availableBondsLoading } = useQuery({
        queryKey: ['ps-available-bonds'],
        queryFn: async () => {
            const resp = await subscriptionAPI.getSubscriptionBonds({ limit: 100 });
            return resp.data;
        },
        staleTime: 60_000
    });

    // Fetch bond IDs that have active sub-agreement templates
    const { data: bondsWithTemplatesResp, isPending: templatesLoading } = useQuery({
        queryKey: ['bonds-with-templates'],
        queryFn: async () => {
            const resp = await subAgreementTemplatesAPI.getBondsWithActiveTemplates();
            return resp.data;
        },
        staleTime: 60_000
    });

    const { data: subAgreementRequestsData = [], isPending: requestsLoading, error: requestsError } = useQuery({
        queryKey: ['ps-sub-agreement-requests'],
        queryFn: async () => {
            const resp = await subAgreementRequestsAPI.getMyRequests();
            const payload = resp?.data?.data;
            if (Array.isArray(payload?.requests)) return payload.requests;
            if (Array.isArray(payload)) return payload;
            return [];
        },
        staleTime: 30_000
    });

    const createRequestMutation = useMutation({
        mutationFn: (payload) => subAgreementRequestsAPI.createRequest(payload),
        onSuccess: () => {
            toast.success('Sub Agreement request submitted.');
            queryClient.invalidateQueries(['ps-sub-agreement-requests']);
            handleCloseSubscriptionModal();
        },
        onError: (error) => {
            const message = error?.response?.data?.message || error?.message || 'Failed to submit sub agreement request.';
            toast.error(message);
        },
        onSettled: () => {
            setIsSubmittingRequest(false);
        }
    });

    const deleteSubmissionMutation = useMutation({
        mutationFn: ({ requestId, submissionId }) => subAgreementRequestsAPI.deleteSubmission(requestId, submissionId),
        onSuccess: (data) => {
            const message = data?.data?.message || 'Submission deleted successfully';
            toast.success(message);
            queryClient.invalidateQueries(['ps-sub-agreement-requests']);
            setShowDeleteModal(false);
            setDeleteTarget(null);
            // Close request details if entire request was deleted
            if (data?.data?.data?.deletedEntireRequest) {
                handleCloseRequestDetails();
            }
        },
        onError: (error) => {
            const message = error?.response?.data?.message || error?.message || 'Failed to delete submission.';
            toast.error(message);
        },
        onSettled: () => {
            setIsDeletingSubmission(false);
        }
    });

    const deleteRequestMutation = useMutation({
        mutationFn: (requestId) => subAgreementRequestsAPI.deleteRequest(requestId),
        onSuccess: () => {
            toast.success('Sub agreement request deleted successfully');
            queryClient.invalidateQueries(['ps-sub-agreement-requests']);
            setShowDeleteModal(false);
            setDeleteTarget(null);
            handleCloseRequestDetails();
        },
        onError: (error) => {
            const message = error?.response?.data?.message || error?.message || 'Failed to delete request.';
            toast.error(message);
        },
        onSettled: () => {
            setIsDeletingRequest(false);
        }
    });

    // Filter eligible bonds (with active or upcoming subscription periods AND have sub-agreement templates)
    const eligibleBonds = useMemo(() => {
        if (!availableBondsResp?.data) return [];

        // Get set of bond IDs that have active sub-agreement templates
        const bondsWithTemplates = new Set(bondsWithTemplatesResp?.data?.bondIds || []);

        const now = new Date();
        return (availableBondsResp.data || []).filter((bond) => {
            const bondId = String(bond._id || bond.id);
            const startDate = bond.subscriptionStartDate ? new Date(bond.subscriptionStartDate) : null;
            const endDate = bond.subscriptionEndDate ? new Date(bond.subscriptionEndDate) : null;
            const hasValidSubscriptionPeriod = startDate && endDate && now >= startDate && now <= endDate;
            const hasSubAgreementTemplate = bondsWithTemplates.has(bondId);

            // Bond must have valid subscription period AND have a sub-agreement template created by FA
            return hasValidSubscriptionPeriod && hasSubAgreementTemplate;
        });
    }, [availableBondsResp, bondsWithTemplatesResp]);

    // Update selected bond data when bond selection changes
    useEffect(() => {
        if (!subscriptionBondId) {
            setSelectedBondData(null);
            return;
        }

        const bond = eligibleBonds.find(b => (b._id || b.id) === subscriptionBondId);
        if (bond) {
            setSelectedBondData(bond);
        }
    }, [subscriptionBondId, eligibleBonds]);

    const handleCloseSubscriptionModal = () => {
        setShowSubscribeModal(false);
        setSubscriptionBondId('');
        setSubscriptionAmount('');
        setIsSubmittingRequest(false);
        setSelectedBondData(null);
    };

    const handleSubmitSubscription = async () => {
        if (isSubmittingRequest || createRequestMutation.isPending) {
            return;
        }

        if (!subscriptionBondId || !subscriptionAmount) {
            toast.error('Please complete all fields before submitting.');
            return;
        }

        if (!selectedBondData) {
            toast.error('Please select a valid bond.');
            return;
        }

        const amountValue = Number(subscriptionAmount);
        if (!Number.isFinite(amountValue) || amountValue <= 0) {
            toast.error('Please enter a valid bond amount.');
            return;
        }

        try {
            setIsSubmittingRequest(true);

            // Generate PDF from backend
            const pdfResponse = await subAgreementRequestsAPI.generatePDF({
                bondId: subscriptionBondId,
                amount: amountValue,
            });

            // Convert blob response to file
            const pdfBlob = pdfResponse.data;
            const bondName = selectedBondData.bondName || selectedBondData.name || 'Bond';
            const pdfFile = new File(
                [pdfBlob],
                `Sub_Agreement_${bondName.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`,
                { type: 'application/pdf' }
            );

            // Upload generated PDF document
            const formData = new FormData();
            formData.append('file', pdfFile);
            formData.append('type', 'document');
            formData.append(
                'relatedTo',
                JSON.stringify({ module: 'bonds', id: subscriptionBondId })
            );
            formData.append(
                'metadata',
                JSON.stringify({ category: 'subAgreementSubmission', bondId: subscriptionBondId })
            );

            const uploadResp = await filesAPI.uploadFile(formData);
            const filePayload = uploadResp?.data?.data?.file || uploadResp?.data?.file || uploadResp?.data;
            const subAgreementFileId = filePayload?._id || filePayload?.id;
            if (!subAgreementFileId) {
                throw new Error('Failed to upload Sub Agreement document.');
            }

            await createRequestMutation.mutateAsync({
                bondId: subscriptionBondId,
                amount: amountValue,
                subAgreementFileId,
            });
        } catch (error) {
            console.error('Sub Agreement request submission failed:', error);
            const message = error?.response?.data?.message || error?.message || 'Failed to submit request.';
            toast.error(message);
            setIsSubmittingRequest(false);
        }
    };

    // Normalize requests data similar to Investor Bond Holdings
    const pendingRequests = useMemo(() => {
        const list = Array.isArray(subAgreementRequestsData) ? subAgreementRequestsData : [];
        return list.map((req) => {
            const submissions = Array.isArray(req.submissions) ? req.submissions : [];
            const latestSubmission = submissions.length > 0 ? submissions[submissions.length - 1] : null;
            // The initial requested amount is from the first submission
            const initialSubmission = submissions.length > 0 ? submissions[0] : null;
            const initialRequestedAmount = initialSubmission?.amount || 0;
            const snapshot = req.bondSnapshot || {};
            const rawStatus = (req.status || 'submitted').toLowerCase();
            return {
                type: 'request',
                id: `request-${req._id}`,
                requestId: req._id,
                bondId: req.bondId,
                bondName: snapshot.bondName || 'Unknown Bond',
                issuer: snapshot.issuer || '—',
                issuerId: snapshot.issuerId || null,
                isin: snapshot.isin || '—',
                stockCode: snapshot.stockCode || '—',
                amount: initialRequestedAmount,
                allocatedAmount: req.allocatedAmount,
                currentValue: req.totalRequestedAmount || 0,
                status: rawStatus,
                statusLabel: rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1),
                latestSubmissionAt: req.latestSubmissionAt || latestSubmission?.submittedAt,
                submissions,
                bondSnapshot: snapshot,
                maturityDate: snapshot.maturityDate || null,
                couponRate: snapshot.couponRate ?? null,
                profitRate: snapshot.profitRate ?? null,
                l1ApproverId: req.l1ApproverId || null,
            };
        });
    }, [subAgreementRequestsData]);

    const requestsData = useMemo(() => {
        return [...pendingRequests].sort((a, b) => {
            const aTime = a.latestSubmissionAt ? new Date(a.latestSubmissionAt).getTime() : 0;
            const bTime = b.latestSubmissionAt ? new Date(b.latestSubmissionAt).getTime() : 0;
            return bTime - aTime;
        });
    }, [pendingRequests]);

    // Reset to first page when pageSize changes
    useEffect(() => { setPage(1); }, [pageSize, requestsData.length]);

    const totalItems = requestsData.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const currentPage = Math.min(page, totalPages);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const displayedRequests = requestsData.slice(startIndex, endIndex);

    const getStatusBadge = (status) => {
        const statusLower = (status || '').toLowerCase();
        const badges = {
            submitted: 'bg-yellow-100 text-yellow-800',
            pending_l1: 'bg-orange-100 text-orange-800',
            pending_l2: 'bg-orange-100 text-orange-800',
            approved: 'bg-green-100 text-green-800',
            rejected: 'bg-red-100 text-red-800',
            cancelled: 'bg-gray-100 text-gray-800',
        };
        const badgeClass = badges[statusLower] || 'bg-gray-100 text-gray-800';
        const statusLabel = statusLower.charAt(0).toUpperCase() + statusLower.slice(1).replace('_', ' ');
        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeClass}`}>
                {statusLabel || status || 'Unknown'}
            </span>
        );
    };

    const handleViewRequestDetails = (request) => {
        setSelectedRequest(request);
        setShowRequestDetails(true);
    };

    const handleCloseRequestDetails = () => {
        setShowRequestDetails(false);
        setSelectedRequest(null);
    };

    const handlePreviewSubmissionSubAgreement = async (submission) => {
        if (!submission.subAgreementFileId) {
            toast.error('Sub Agreement document not available.');
            return;
        }

        try {
            const response = await filesAPI.downloadFile(submission.subAgreementFileId);

            // Determine the correct MIME type from response headers or default to PDF
            const contentType = response.headers['content-type'] || 'application/pdf';

            // Create blob with proper type
            const blob = new Blob([response.data], { type: contentType });
            const url = window.URL.createObjectURL(blob);

            // Open in new window
            window.open(url, '_blank', 'noopener,noreferrer');

            // Clean up the URL after a delay to ensure the window has loaded
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
            }, 1000);
        } catch (error) {
            console.error('Error previewing Sub Agreement:', error);
            const message = error?.response?.data?.message || error?.message || 'Failed to preview Sub Agreement document.';
            toast.error(message);
        }
    };

    const handleDownloadSubmissionSubAgreement = async (submission) => {
        if (!submission.subAgreementFileId) {
            toast.error('Sub Agreement document not available.');
            return;
        }

        try {
            setSubmissionDownloadId(submission.subAgreementFileId);
            const response = await filesAPI.downloadFile(submission.subAgreementFileId);
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Sub_Agreement_${selectedRequest?.bondName || 'document'}_${submission.submittedAt ? formatDate(submission.submittedAt) : 'download'}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success('Sub Agreement downloaded successfully');
        } catch (error) {
            console.error('Error downloading Sub Agreement:', error);
            toast.error('Failed to download Sub Agreement document.');
        } finally {
            setSubmissionDownloadId(null);
        }
    };

    const handleDeleteSubmission = (requestId, submissionId) => {
        setDeleteTarget({ type: 'submission', requestId, submissionId });
        setShowDeleteModal(true);
    };

    const handleDeleteRequest = (requestId) => {
        setDeleteTarget({ type: 'request', requestId });
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;

        if (deleteTarget.type === 'submission') {
            setIsDeletingSubmission(true);
            await deleteSubmissionMutation.mutateAsync({
                requestId: deleteTarget.requestId,
                submissionId: deleteTarget.submissionId
            });
        } else if (deleteTarget.type === 'request') {
            setIsDeletingRequest(true);
            await deleteRequestMutation.mutateAsync(deleteTarget.requestId);
        }
    };

    const handleCancelDelete = () => {
        setShowDeleteModal(false);
        setDeleteTarget(null);
    };

    // Check if a request can be deleted (not L1 approved and not already finalized)
    const canDeleteRequest = (request) => {
        const finalizedStatuses = ['approved', 'rejected', 'cancelled'];
        return !request.l1ApproverId && !finalizedStatuses.includes(request.status);
    };

    if (requestsLoading && requestsData.length === 0) {
        return <LoadingSpinner size="lg" text="Loading sub agreement requests..." />;
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
                <div className="space-y-6">
                    {/* Page Header */}
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Submit Sub Agreement</h1>
                            <p className="mt-1 text-secondary-600">
                                Submit Sub Agreement requests for bonds
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setShowSubscribeModal(true)}

                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                            Subscribe to Bond
                        </button>
                    </div>

                    {/* Requests Table */}
                    <div className="card">
                        <div className="card-header">
                            <div className="flex items-center justify-between w-full">
                                <h3>Sub Agreement Requests</h3>
                                <button className="btn-primary btn-sm">
                                    <Download className="w-4 h-4 mr-2" />
                                    Export
                                </button>
                            </div>
                        </div>
                        <div className="p-0 card-body">
                            <div className="overflow-x-auto">
                                <table className="table">
                                    <thead className="table-header">
                                        <tr>
                                            <th className="table-header-cell">Bond Name</th>
                                            <th className="table-header-cell">ISIN</th>
                                            <th className="table-header-cell">Stock Code</th>
                                            <th className="table-header-cell">Issue Date</th>
                                            <th className="table-header-cell">Maturity Date</th>
                                            <th className="table-header-cell">Requested Amount</th>
                                            <th className="table-header-cell">Allocated Amount</th>
                                            <th className="table-header-cell">Status</th>
                                            <th className="table-header-cell">Submitted</th>
                                            <th className="table-header-cell">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {requestsLoading && displayedRequests.length === 0 && (
                                            <tr>
                                                <td colSpan="10" className="py-12 text-center text-secondary-500">
                                                    <LoadingSpinner size="md" text="Loading requests..." />
                                                </td>
                                            </tr>
                                        )}
                                        {requestsError && !requestsLoading && (
                                            <tr>
                                                <td colSpan="10" className="py-12 text-center text-error-600">
                                                    Failed to load requests: {requestsError.message}
                                                </td>
                                            </tr>
                                        )}
                                        {!requestsLoading && !requestsError && displayedRequests.length === 0 && (
                                            <tr>
                                                <td colSpan="10" className="py-12 text-center text-secondary-500">
                                                    <div className="flex flex-col items-center space-y-2">
                                                        <BarChart3 className="w-10 h-10 text-gray-300" />
                                                        <p className="text-sm">No sub agreement requests yet</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                        {displayedRequests.map((request) => (
                                            <tr key={request.id} className="transition-colors border-b border-light-gray hover:bg-secondary-50">
                                                <td className="px-4 py-4">
                                                    <div>
                                                        <div className="text-sm font-semibold leading-5 text-dark-gray-900">
                                                            {request.bondName}
                                                        </div>
                                                        <div className="mt-1 text-xs text-secondary-500">
                                                            {request.issuer} | {request.bondType || 'Bond'}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4 font-mono text-sm text-secondary-600">
                                                    {request.isin || '—'}
                                                </td>
                                                <td className="px-4 py-4 font-mono text-sm text-secondary-600">
                                                    {request.stockCode || '—'}
                                                </td>
                                                <td className="px-4 py-4 text-sm text-secondary-600">
                                                    {request.bondSnapshot?.issueDate ? formatDate(request.bondSnapshot.issueDate) : '—'}
                                                </td>
                                                <td className="px-4 py-4 text-sm text-secondary-600">
                                                    {request.maturityDate ? formatDate(request.maturityDate) : '—'}
                                                </td>
                                                <td className="px-4 py-4 text-sm text-secondary-600">
                                                    {formatCurrency(request.amount)}
                                                </td>
                                                <td className="px-4 py-4 text-sm text-secondary-600">
                                                    {request.allocatedAmount != null ? (
                                                        <div className="flex flex-col">
                                                            <span className="font-semibold text-gray-900">
                                                                {formatCurrency(request.allocatedAmount)}
                                                            </span>
                                                            {request.allocatedAmount !== request.amount && (
                                                                <span className="text-xs">
                                                                    {request.allocatedAmount > request.amount ? (
                                                                        <span className="text-green-600">
                                                                            +{formatCurrency(request.allocatedAmount - request.amount)}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-amber-600">
                                                                            {formatCurrency(request.allocatedAmount - request.amount)}
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="text-secondary-400">—</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-4">
                                                    {getStatusBadge(request.status)}
                                                </td>
                                                <td className="px-4 py-4 text-sm text-secondary-600">
                                                    {request.latestSubmissionAt ? formatDate(request.latestSubmissionAt) : '—'}
                                                </td>
                                                <td className="px-4 py-4 text-center">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <button
                                                            onClick={() => handleViewRequestDetails(request)}
                                                            className="inline-flex items-center justify-center w-8 h-8 transition-colors rounded-lg hover:bg-secondary-200 text-primary-600 hover:text-secondary-700"
                                                            title="View Request Details"
                                                        >
                                                            <Eye className="w-4 h-4" />
                                                        </button>
                                                        {canDeleteRequest(request) && (
                                                            <button
                                                                onClick={() => handleDeleteRequest(request.requestId)}

                                                                className="inline-flex items-center justify-center w-8 h-8 text-red-600 transition-colors rounded-lg hover:bg-red-100 hover:text-red-700"
                                                                title="Delete Request"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {displayedRequests.length > 0 && (
                                    <div className="px-6 py-3 border-t bg-secondary-50 border-light-gray">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center text-sm text-secondary-500">
                                                <span>
                                                    Showing {displayedRequests.length === 0 ? 0 : startIndex + 1} to{' '}
                                                    {Math.min(endIndex, totalItems)} of{' '}
                                                    {totalItems} results
                                                </span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                                    disabled={currentPage === 1}
                                                    className="p-2 bg-white border rounded-md border-secondary-300 text-secondary-500 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <ChevronLeft className="w-4 h-4" />
                                                </button>

                                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => setPage(pageNum)}
                                                        className={`px-3 py-1 rounded-md text-sm font-medium ${pageNum === currentPage
                                                            ? 'bg-primary-600 text-white'
                                                            : 'bg-white border border-secondary-300 text-secondary-700 hover:bg-secondary-50'
                                                            }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                ))}

                                                <button
                                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                                    disabled={currentPage === totalPages}
                                                    className="p-2 bg-white border rounded-md border-secondary-300 text-secondary-500 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <ChevronRight className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Request Details Modal */}
                    {showRequestDetails && selectedRequest && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                            <div className="bg-white rounded-lg w-full max-w-3xl mx-4 sm:mx-6 lg:mx-0 max-h-[90vh] overflow-y-auto shadow-xl">
                                <div className="flex items-start justify-between px-6 py-5 border-b border-light-gray">
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold text-gray-900">Sub Agreement Request Details</h3>
                                        <p className="mt-1 text-sm text-secondary-600">
                                            Review the history of Sub Agreement submissions and requested amounts for this bond.
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {canDeleteRequest(selectedRequest) && (
                                            <button
                                                onClick={() => handleDeleteRequest(selectedRequest.requestId)}

                                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-md bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                                title="Delete Entire Request"
                                            >
                                                <Trash2 className="w-4 h-4 mr-1" />
                                                Delete Request
                                            </button>
                                        )}
                                        <button
                                            onClick={handleCloseRequestDetails}
                                            className="text-gray-400 hover:text-gray-600"
                                            aria-label="Close request details"
                                        >
                                            <X className="w-6 h-6" />
                                        </button>
                                    </div>
                                </div>
                                <div className="px-6 py-6 space-y-6">
                                    <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 text-secondary-700">
                                        <div>
                                            <span className="block font-medium text-secondary-500">Bond</span>
                                            <span className="font-semibold text-gray-900">{selectedRequest.bondName}</span>
                                        </div>
                                        <div>
                                            <span className="block font-medium text-secondary-500">Status</span>
                                            {getStatusBadge(selectedRequest.status)}
                                        </div>
                                        <div>
                                            <span className="block font-medium text-secondary-500">Total Requested</span>
                                            <span className="font-semibold text-gray-900">{formatCurrency(selectedRequest.amount)}</span>
                                        </div>
                                        {selectedRequest.allocatedAmount != null && (
                                            <div>
                                                <span className="block font-medium text-secondary-500">Allocated Amount</span>
                                                <div className="flex flex-col">
                                                    <span className="font-semibold text-gray-900">
                                                        {formatCurrency(selectedRequest.allocatedAmount)}
                                                    </span>
                                                    {selectedRequest.allocatedAmount !== selectedRequest.amount && (
                                                        <span className="text-xs">
                                                            {selectedRequest.allocatedAmount > selectedRequest.amount ? (
                                                                <span className="text-green-600">
                                                                    +{formatCurrency(selectedRequest.allocatedAmount - selectedRequest.amount)} (Increased)
                                                                </span>
                                                            ) : (
                                                                <span className="text-amber-600">
                                                                    {formatCurrency(selectedRequest.allocatedAmount - selectedRequest.amount)} (Reduced)
                                                                </span>
                                                            )}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        <div>
                                            <span className="block font-medium text-secondary-500">Latest Submission</span>
                                            <span className="text-gray-900">
                                                {selectedRequest.latestSubmissionAt ? formatDate(selectedRequest.latestSubmissionAt) : '—'}
                                            </span>
                                        </div>
                                    </div>

                                    <div>
                                        <h4 className="mb-3 text-sm font-medium text-gray-700">Submission History</h4>
                                        {selectedRequest.submissions && selectedRequest.submissions.length > 0 ? (
                                            <div className="overflow-x-auto border rounded-md border-light-gray">
                                                <table className="min-w-full text-sm table-auto">
                                                    <thead className="text-xs tracking-wide uppercase bg-secondary-50 text-secondary-600">
                                                        <tr>
                                                            <th className="px-4 py-3 text-left">Submitted On</th>
                                                            <th className="px-4 py-3 text-left">Bond Amount</th>
                                                            <th className="px-4 py-3 text-left">Sub Agreement Document</th>
                                                            {canDeleteRequest(selectedRequest) && (
                                                                <th className="px-4 py-3 text-center">Actions</th>
                                                            )}
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-light-gray">
                                                        {selectedRequest.submissions.map((submission, index) => {
                                                            const submissionId = submission._id || submission.id || submission.subAgreementFileId;
                                                            return (
                                                                <tr key={`${selectedRequest.requestId}-submission-${index}`} className="bg-white">
                                                                    <td className="px-4 py-3 text-gray-700">
                                                                        {submission.submittedAt ? formatDate(submission.submittedAt) : '—'}
                                                                    </td>
                                                                    <td className="px-4 py-3 text-gray-700">
                                                                        {formatCurrency(submission.amount || 0)}
                                                                    </td>
                                                                    <td className="px-4 py-3 text-secondary-700">
                                                                        <div className="flex items-center space-x-2">
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handlePreviewSubmissionSubAgreement(submission)}
                                                                                className="btn-secondary btn-sm"
                                                                                disabled={!submission.subAgreementFileId}
                                                                            >
                                                                                Preview
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleDownloadSubmissionSubAgreement(submission)}
                                                                                className="btn-primary btn-sm"
                                                                                disabled={!submission.subAgreementFileId || submissionDownloadId === submission.subAgreementFileId}
                                                                            >
                                                                                {submissionDownloadId === submission.subAgreementFileId ? 'Downloading…' : 'Download'}
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                    {canDeleteRequest(selectedRequest) && (
                                                                        <td className="px-4 py-3 text-center">
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleDeleteSubmission(selectedRequest.requestId, submissionId)}

                                                                                className="inline-flex items-center justify-center w-8 h-8 text-red-600 transition-colors rounded-lg hover:bg-red-100 hover:text-red-700"
                                                                                title="Delete Submission"
                                                                            >
                                                                                <Trash2 className="w-4 h-4" />
                                                                            </button>
                                                                        </td>
                                                                    )}
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <p className="text-sm text-secondary-600">No submissions recorded yet.</p>
                                        )}
                                    </div>
                                </div>
                                <div className="flex justify-end px-6 py-4 border-t border-light-gray">
                                    <button
                                        type="button"
                                        onClick={handleCloseRequestDetails}
                                        className="btn-secondary"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Subscribe Modal */}
                    {showSubscribeModal && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                            <div className="bg-white rounded-lg w-full max-w-3xl mx-4 sm:mx-6 lg:mx-0 max-h-[90vh] overflow-y-auto shadow-xl">
                                <div className="flex items-start justify-between px-6 py-5 border-b border-light-gray">
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900">Submit Sub Agreement</h3>
                                        <p className="mt-1 text-sm text-secondary-600">
                                            Select an eligible bond, specify the desired bond amount, and provide the Sub Agreement.
                                        </p>
                                    </div>
                                    <button
                                        onClick={handleCloseSubscriptionModal}
                                        className="text-gray-400 hover:text-gray-600"
                                        aria-label="Close subscription modal"
                                    >
                                        <X className="w-6 h-6" />
                                    </button>
                                </div>
                                <div className="px-6 py-6 space-y-6">
                                    <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                            Select Bond
                                        </label>
                                        <select
                                            className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                                            value={subscriptionBondId}
                                            onChange={(e) => {
                                                setSubscriptionBondId(e.target.value);
                                            }}
                                        >
                                            <option value="">
                                                {availableBondsLoading || templatesLoading
                                                    ? 'Loading eligible bonds...'
                                                    : eligibleBonds.length === 0
                                                        ? 'No eligible bonds available'
                                                        : 'Choose a bond'}
                                            </option>
                                            {eligibleBonds.map((bond) => (
                                                <option key={bond._id || bond.id} value={bond._id || bond.id}>
                                                    {bond.bondName || bond.name || 'Unnamed Bond'}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                            Bond Amount
                                        </label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                                            placeholder="Enter desired bond amount"
                                            value={subscriptionAmount}
                                            onChange={(e) => setSubscriptionAmount(e.target.value)}
                                        />
                                        <p className="text-xs text-secondary-500">
                                            Specify the bond amount you wish to subscribe for this bond.
                                        </p>
                                    </div>

                                    {selectedBondData && (
                                        <div className="pt-4 border-t border-gray-200">
                                            <h4 className="mb-3 text-sm font-medium text-gray-900">
                                                Bond Details
                                            </h4>
                                            <div className="p-4 space-y-2 text-sm rounded-md bg-gray-50">
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Bond Name:</span>
                                                    <span className="font-medium text-gray-900">{selectedBondData.bondName || selectedBondData.name || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">ISIN:</span>
                                                    <span className="font-medium text-gray-900">{selectedBondData.isin || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Stock Code:</span>
                                                    <span className="font-medium text-gray-900">{selectedBondData.stockCode || 'N/A'}</span>
                                                </div>
                                            </div>
                                            <p className="mt-2 text-xs text-secondary-500">
                                                A Sub Agreement document will be automatically generated with these bond details.
                                            </p>
                                        </div>
                                    )}                                </div>
                                <div className="flex flex-col px-6 py-4 space-y-3 border-t border-light-gray sm:flex-row sm:justify-end sm:space-x-3 sm:space-y-0">
                                    <button
                                        type="button"
                                        onClick={handleCloseSubscriptionModal}
                                        className="btn-secondary"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSubmitSubscription}
                                        className="btn-primary"
                                        disabled={!subscriptionBondId || !subscriptionAmount || !selectedBondData || isSubmittingRequest || createRequestMutation.isPending}
                                    >
                                        {isSubmittingRequest || createRequestMutation.isPending ? 'Submitting…' : 'Submit Request'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Delete Confirmation Modal */}
                    {showDeleteModal && deleteTarget && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                            <div className="w-full max-w-md mx-4 bg-white rounded-lg shadow-xl">
                                <div className="px-6 py-5 border-b border-light-gray">
                                    <h3 className="text-lg font-semibold text-gray-900">
                                        {deleteTarget.type === 'submission' ? 'Delete Submission' : 'Delete Request'}
                                    </h3>
                                </div>
                                <div className="px-6 py-4">
                                    <p className="text-sm text-gray-700">
                                        {deleteTarget.type === 'submission'
                                            ? 'Are you sure you want to delete this submission? This action cannot be undone.'
                                            : 'Are you sure you want to delete this entire request? All submissions will be removed. This action cannot be undone.'}
                                    </p>
                                </div>
                                <div className="flex justify-end px-6 py-4 space-x-3 border-t border-light-gray">
                                    <button
                                        type="button"
                                        onClick={handleCancelDelete}
                                        className="btn-secondary"
                                        disabled={isDeletingSubmission || isDeletingRequest}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleConfirmDelete}
                                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                        disabled={isDeletingSubmission || isDeletingRequest}
                                    >
                                        {isDeletingSubmission || isDeletingRequest ? (
                                            <>
                                                <LoadingSpinner size="sm" className="mr-2" />
                                                Deleting…
                                            </>
                                        ) : (
                                            <>
                                                <Trash2 className="w-4 h-4 mr-2" />
                                                Delete
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default SubmitSubAgreement;

